import {api} from './api';
import {isAxiosError} from 'axios';

const API_BASE_URL = '/api/v1/agents';

class AgentConfigService {
    // 获取所有智能体配置
    async getAllAgentConfigs(): Promise<any[]> {
        try {
            const response = await api.get(`${API_BASE_URL}`);
            return response.data.data;
        } catch (error) {
            // console.error('获取所有智能体配置失败:', error);
            throw error;
        }
    }

    // 获取所有活跃的智能体配置
    async getActiveAgentConfigs(): Promise<any[]> {
        try {
            const response = await api.get(`${API_BASE_URL}/active`);
            return response.data.data;
        } catch (error) {
            // console.error('获取活跃智能体配置失败:', error);
            throw error;
        }
    }

    // 根据ID获取智能体配置
    async getAgentConfigById(id: number): Promise<any> {
        try {
            const response = await api.get(`${API_BASE_URL}/${id}`);
            return response.data.data;
        } catch (error) {
            // console.error('根据ID获取智能体配置失败:', error);
            throw error;
        }
    }

    // 创建智能体配置
    async createAgentConfig(config: any): Promise<any> {
        try {
            const response = await api.post(`${API_BASE_URL}`, config);
            return response.data.data;
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || '创建智能体配置失败';
                throw new Error(errorMessage);
            }
            // console.error('创建智能体配置失败:', error);
            throw error;
        }
    }

    // 更新智能体配置
    async updateAgentConfig(id: number, config: any): Promise<any> {
        try {
            const response = await api.put(`${API_BASE_URL}/${id}`, config);
            return response.data.data;
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || '更新智能体配置失败';
                throw new Error(errorMessage);
            }
            // console.error('更新智能体配置失败:', error);
            throw error;
        }
    }

    // 删除智能体配置
    async deleteAgentConfig(id: number): Promise<void> {
        try {
            await api.delete(`${API_BASE_URL}/${id}`);
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || '删除智能体配置失败';
                throw new Error(errorMessage);
            }
            // console.error('删除智能体配置失败:', error);
            throw error;
        }
    }

    // 获取可用的工具列表
    async getAvailableTools(): Promise<Array<{
        name: string;
        label?: string;
        description: string;
        parameters?: string
    }>> {
        try {
            const response = await api.get(`${API_BASE_URL}/tools`);
            return response.data.data;
        } catch (error) {
            // console.error('获取可用工具列表失败:', error);
            throw error;
        }
    }

    // 获取可绑定的模型列表
    async getAvailableModels(): Promise<Array<{
        configId: number;
        modelId: string;
        modelName: string;
        modelType?: string;
        provider: string
    }>> {
        try {
            const response = await api.get(`${API_BASE_URL}/models`);
            return response.data.data;
        } catch (error) {
            // console.error('获取可用模型列表失败:', error);
            throw error;
        }
    }

    // 获取指定智能体的触发配置
    async getAgentTriggers(agentId: number): Promise<any[]> {
        try {
            const response = await api.get(`${API_BASE_URL}/${agentId}/triggers`);
            return response.data.data;
        } catch (error) {
            throw error;
        }
    }

    // 获取指定智能体的产出配置
    async getAgentOutputs(agentId: number): Promise<any[]> {
        try {
            const response = await api.get(`${API_BASE_URL}/${agentId}/outputs`);
            return response.data.data;
        } catch (error) {
            throw error;
        }
    }

    // 获取指定Agent的子Agent列表
    async getSubAgents(agentId: number): Promise<any[]> {
        try {
            const response = await api.get(`/api/v1/agents/relations/${agentId}`);
            return response.data.data;
        } catch (error) {
            // console.error('获取子Agent列表失败:', error);
            throw error;
        }
    }

    // 获取智能体招呼语配置
    async getAgentGreeting(agentId: number): Promise<any> {
        try {
            const response = await api.get(`${API_BASE_URL}/${agentId}/greeting`);
            return response.data.data;
        } catch (error) {
            throw error;
        }
    }

    // 保存智能体招呼语配置
    async saveAgentGreeting(agentId: number, config: any): Promise<any> {
        try {
            const response = await api.put(`${API_BASE_URL}/${agentId}/greeting`, config);
            return response.data.data;
        } catch (error) {
            throw error;
        }
    }

    // 获取版本历史
    async getVersionHistory(agentId: number): Promise<Array<{
        id: number;
        agentConfigId: number;
        versionNumber: number;
        changeDescription: string;
        createdAt: string;
    }>> {
        try {
            const response = await api.get(`${API_BASE_URL}/${agentId}/versions`);
            return response.data.data;
        } catch (error) {
            throw error;
        }
    }

    // 回滚到指定版本
    async restoreVersion(agentId: number, versionId: number): Promise<any> {
        try {
            const response = await api.post(`${API_BASE_URL}/${agentId}/versions/${versionId}/restore`);
            return response.data.data;
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || '回滚版本失败';
                throw new Error(errorMessage);
            }
            throw error;
        }
    }

    // 删除智能体招呼语配置
    async deleteAgentGreeting(agentId: number): Promise<void> {
        try {
            await api.delete(`${API_BASE_URL}/${agentId}/greeting`);
        } catch (error) {
            throw error;
        }
    }

    // 保存Agent关系
    async saveAgentRelation(relation: any): Promise<any> {
        try {
            const response = await api.post('/api/v1/agents/relations', relation);
            return response.data.data;
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || '保存Agent关系失败';
                throw new Error(errorMessage);
            }
            // console.error('保存Agent关系失败:', error);
            throw error;
        }
    }

    // 删除Agent关系
    async deleteAgentRelation(id: number): Promise<void> {
        try {
            await api.delete(`/api/v1/agents/relations/${id}`);
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || '删除Agent关系失败';
                throw new Error(errorMessage);
            }
            // console.error('删除Agent关系失败:', error);
            throw error;
        }
    }
}

export const agentConfigService = new AgentConfigService();
